export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatInches(px: number, dpi: number): string {
  return `${(px / dpi).toFixed(2)} in`;
}

/** <base>_<garment>_<w>x<h>in_<dpi>dpi.png, e.g. skull_000000_11.00x9.50in_300dpi.png */
export function exportFileName(name: string, garmentHex: string, widthPx: number, heightPx: number, dpi: number): string {
  const base = name.replace(/\.[^.]+$/, '').replace(/[^\w.-]+/g, '_');
  const g = garmentHex.replace(/^#/, '').toLowerCase();
  const w = (widthPx / dpi).toFixed(2), h = (heightPx / dpi).toFixed(2);
  return `${base}_${g}_${w}x${h}in_${Math.round(dpi)}dpi.png`;
}

/**
 * Does the viewer's HUD offer the "Background: Garment / Checker / White" group in
 * this mode?
 *
 * Not in Mockup. There the canvas is an opaque photograph of a garment edge to edge —
 * the three buttons change what is painted BEHIND it, which is nothing anybody can
 * see, so the row asked a question with no answer. (The garment's own backdrop is the
 * panel's Backdrop section, which is a different thing entirely and says so.) In DTF
 * and Screen print the art has transparency and the choice is the whole point: it is
 * how a halo or a stray speck is seen at all.
 *
 * A predicate rather than a `&&` in the markup so the rule is one testable fact
 * instead of a condition nobody can reach without a browser.
 */
export function hudShowsBackground(mode: 'dtf' | 'sep' | 'mockup'): boolean {
  return mode !== 'mockup';
}

/**
 * The viewer HUD's right-hand readout. "Open a file to begin" is only for the
 * empty state: once a document is open its first preview may still be a few
 * seconds away, and the HUD should say what is happening ("running…") rather
 * than keep inviting the user to open something they already opened.
 *
 * `unit` and `status` are the two extra parts the screen-print and mockup views
 * need: both measure in their own pixels rather than the art's (`unit: 'px'` —
 * film pixels at 1:1, garment-photo pixels for a mockup), and `status` carries
 * whatever that view has to say about what is on screen — "film 1:1",
 * "rebuilding…", "stale" for a separation; the blank's name plus "rendering…",
 * "stale" and "clipped" for a mockup. Terms are joined with the same " · " the
 * caller composes them with. Both fields are optional, so every DTF caller is
 * unchanged.
 */
export function hudText(o: { size: { width: number; height: number } | null; open: boolean; running: boolean; zoom: number | null; unit?: string; status?: string }): string {
  const parts: string[] = [];
  if (o.size) parts.push(`${o.size.width}×${o.size.height}${o.unit ? ` ${o.unit}` : ''}`);
  else if (!o.open) parts.push('Open a file to begin');
  else if (!o.running) parts.push('preparing…');
  if (o.zoom !== null) parts.push(`${Math.round(o.zoom * 100)}%`);
  if (o.running) parts.push('running…');
  if (o.status) parts.push(o.status);
  return parts.join(' · ');
}
